import { useRef, useState, type KeyboardEvent } from 'react';

/**
 * Navigation au CLAVIER dans une liste de suggestions.
 *
 * Un champ saisissable n'a d'interet que si la main ne quitte pas le
 * clavier : on tape « Zo », on descend d'une fleche, on valide. Aller
 * chercher la souris pour choisir entre quatre reponses annule le gain.
 *
 * Trois touches, et chacune a sa raison :
 *   - FLECHES haut/bas : deplacent le choix, en boucle. `preventDefault`
 *     empeche le curseur de sauter au bout du texte saisi ;
 *   - ENTREE : valide. `preventDefault` est ici VITAL — ces champs
 *     vivent dans des formulaires, et sans lui la touche enregistrerait
 *     l'inscription au lieu de choisir l'adherent ;
 *   - TAB : valide AUSSI, mais sans preventDefault : le focus continue
 *     sa route vers le champ suivant, ce qu'on attend de Tab. Il ne
 *     valide toutefois QUE si l'on a navigue aux fleches — traverser un
 *     champ ouvert ne doit pas y poser la premiere suggestion venue.
 *
 * L'element survole est ramene dans la fenetre de defilement : une liste
 * de douze suggestions en montre six, et la selection sortait du cadre
 * sans qu'on sache ou elle etait.
 */
export function useListeClavier<T>(elements: T[], onChoisir: (element: T) => void) {
  const [index, setIndex] = useState(0);
  // Tab ne choisit QUE si l'on a navigue dans la liste. Sans cette
  // condition, traverser un champ ouvert au clavier y poserait la
  // premiere suggestion venue, sans que personne l'ait demandee.
  const [navigue, setNavigue] = useState(false);
  const liste = useRef<HTMLUListElement>(null);

  // La liste change a chaque frappe : un index qui la depasse pointerait
  // dans le vide, et Entree ne choisirait rien.
  const courant = index < elements.length ? index : 0;

  const deplacer = (pas: number) => {
    if (elements.length === 0) return;
    const suivant = (courant + pas + elements.length) % elements.length;
    setIndex(suivant);
    setNavigue(true);
    liste.current
      ?.querySelector(`[data-index="${suivant}"]`)
      ?.scrollIntoView({ block: 'nearest' });
  };

  const auClavier = (evenement: KeyboardEvent<HTMLInputElement>) => {
    if (elements.length === 0) return;
    if (evenement.key === 'ArrowDown') {
      evenement.preventDefault();
      deplacer(1);
    } else if (evenement.key === 'ArrowUp') {
      evenement.preventDefault();
      deplacer(-1);
    } else if (evenement.key === 'Enter') {
      evenement.preventDefault();
      onChoisir(elements[courant]!);
    } else if (evenement.key === 'Tab' && navigue) {
      onChoisir(elements[courant]!);
    }
  };

  return {
    /** Index survole — a marquer visuellement sur l'element correspondant. */
    index: courant,
    /** Le survol suit aussi la souris, sinon les deux se contredisent. */
    survoler: (rang: number) => {
      setIndex(rang);
      setNavigue(true);
    },
    /** A poser sur le `<ul>` : sert a ramener l'element choisi dans le cadre. */
    liste,
    auClavier,
  };
}

/** La classe d'un element survole, pour que les listes se ressemblent. */
export const CLASSE_SURVOL = 'bg-encre-100';
